import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Rss, Calendar, Loader2, Radio } from 'lucide-react';

function Feed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchFeed() {
      try {
        const res = await api.get('/feed?limit=50');
        setPosts(res.data);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchFeed();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ruby" />
      </div>
    );
  }

  const postCards = [];
  for (let i = 0; i < posts.length; i++) {
    const post = posts[i];
    const date = new Date(post.published_at);
    const dateStr = date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    postCards.push(
      <article key={post.id} className="bg-surface rounded-xl border border-border p-6">
        <Link to={'/channel/' + post.channel_id} className="flex items-center gap-3 mb-4 group">
          <div className="w-10 h-10 rounded-full overflow-hidden">
            <img 
              src={post.channel_avatar || 'https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=200'} 
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="font-medium text-foreground group-hover:text-ruby transition-colors">{post.channel_name}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {dateStr} às {timeStr}
            </p>
          </div>
        </Link>

        <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">{post.content}</p>

        {post.image_url && (
          <div className="mt-4 rounded-lg overflow-hidden">
            <img src={post.image_url} alt="" className="w-full max-h-96 object-cover" />
          </div>
        )}
      </article>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
                <Rss className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="font-brand text-2xl font-bold text-foreground">Meu Feed</h1>
                <p className="text-muted-foreground">Posts dos canais que segue</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={function() { navigate('/channels'); }}
              className="border-border"
            >
              <Radio className="w-4 h-4 mr-2" />
              Ver Canais
            </Button>
          </div>

          <div className="space-y-6">
            {posts.length > 0 ? postCards : (
              <div className="text-center py-20 bg-surface rounded-xl border border-border">
                <Rss className="w-16 h-16 text-muted mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-foreground mb-2">Seu feed está vazio</h2>
                <p className="text-muted-foreground mb-6">Segue alguns canais para ver posts aqui.</p>
                <Button
                  onClick={function() { navigate('/channels'); }}
                  data-testid="discover-channels"
                  className="bg-ruby hover:bg-ruby-light rounded-full"
                >
                  Descobrir Canais
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Feed;
